/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Thu Sep 07 2023
 **/

import { useEffect, useState } from "react";
import { Subscription, timer } from "rxjs";

import { Column, Sidebar } from "@/frameworks/designStuff/components";
import { useOutsideClickEffect } from "@/frameworks/libraries/hooks";

import { SidebarID } from "../../commons";
import { useSidebarActions } from "../../hooks";

type SidebarItemProps = React.ComponentProps<typeof Sidebar>;

export default function SidebarItem(props: SidebarItemProps) {
	const { body, isOpen, sidebarId } = props;
	const [isRender, setIsRender] = useState(isOpen);

	// Sidebar가 열리거나 닫힐 때 애니메이션을 위해 render 여부를 조절
	useEffect(() => {
		let timeoutId: Subscription | null = null;
		if (isOpen) {
			setIsRender(true);
		} else {
			timeoutId = timer(300).subscribe(() => {
				setIsRender(false);
			});
		}

		return () => {
			timeoutId?.unsubscribe();
		};
	}, [isOpen]);

	return <Sidebar {...props} body={isRender ? <DynamicSidebarItem sidebarId={sidebarId} body={body} /> : <></>} />;
}

function DynamicSidebarItem({ body, sidebarId }: { body: JSX.Element; sidebarId: SidebarID }) {
	const sidebarActions = useSidebarActions();

	// 활성화된 Sidebar의 외부 클릭 감지
	const { ref } = useOutsideClickEffect(() => {
		// console.debug("outside click");
	}, []);

	useEffect(() => {
		const handleEscapeAllLeftSideBar = (ev: KeyboardEvent) => {
			if (ev.key !== "Escape") return;

			if (
				sidebarActions.getFocus() &&
				(
					[
						SidebarID.Chat,
						SidebarID.Meeting,
						SidebarID.People,
						SidebarID.Phone,
						SidebarID.Space,
						SidebarID.Activity
					] as SidebarID[]
				).includes(sidebarActions.getFocus()!)
			) {
				sidebarActions.hideAll("left");
				return;
			} else {
				sidebarActions.hide(sidebarActions.getFocus() as SidebarID);
			}
		};

		window.addEventListener("keydown", handleEscapeAllLeftSideBar);
		return () => {
			window.removeEventListener("keydown", handleEscapeAllLeftSideBar);
		};
	}, [sidebarActions]);

	return (
		<Column
			onMouseDown={() => {
				sidebarActions.setFocus(sidebarId);
			}}
			css={{
				height: "100%",
				width: "100%"
			}}
			ref={ref}
		>
			{body}
		</Column>
	);
}
